import { Injectable } from '@angular/core';
import { Observable, of, from } from 'rxjs';
import { switchMap, take, map } from 'rxjs/operators';
import { AngularFireAuth } from '@angular/fire/auth';
import { AngularFirestore, AngularFirestoreDocument } from '@angular/fire/firestore';
import * as firebase from 'firebase/app';
import User from '../interfaces/User';
import { ToastService } from './toast.service';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  user: Observable<any>;

  constructor(
    private afAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private router: Router,
    private toastService: ToastService,
    private http: HttpClient
  ) {
    this.setUser();
  }

  setUser() {
    this.user = this.afAuth.authState.pipe(
      switchMap((user) => {
        if (user) {
          return this.afs.doc(`users/${user.uid}`).valueChanges();
        } else {
          return of(null);
        }
      })
    );
  }

  logout(disableAccount = false) {
    this.afAuth.auth.signOut().then(() => {
      this.setUser();
      this.router.navigate(['/']);
    });
  }

  loginWithEmailAndPassword(e, p) {
    return from(this.afAuth.auth.signInWithEmailAndPassword(e, p));
  }

  signup(displayName: string, email: string, password: string, newsletter: boolean): Promise<void> {
    return this.afAuth.auth.createUserWithEmailAndPassword(email, password).then((credential) => {
      return this.createUser(credential.user, displayName, newsletter);
    });
  }

  createUser(user, dn, n): Promise<void> {
    const userRef: AngularFirestoreDocument<any> = this.afs.doc(`users/${user.uid}`);
    const notifications = {
      newDiscussions: true,
      directMentions: true,
      replies: true,
      repliesToFollowing: true,
      likes: false,
      newResources: false,
      resourceDiscussions: true,
      newProjects: false,
      newInstitutions: false,
      newEvents: true,
      newJobs: false,
    };
    const userData: User = {
      uid: user.uid,
      email: user.email,
      displayName: dn,
      firstName: dn.split(' ')[0],
      lastName: dn.split(' ').length > 1 ? dn.split(' ').slice(-1)[0] : '',
      bio: null,
      country: null,
      comments: [],
      documentStats: null,
      expertise: null,
      events: [],
      following: null,
      institution: null,
      institutions: [],
      institutionRoles: {},
      interests: null,
      jobTitle: null,
      jobSector: null,
      newsletter: n,
      notificationSettings: notifications,
      profilePicture: null,
      projects: [],
      projectRoles: {},
      role: null,
      title: null,
      webpage: null,
      disabled: false,
      bookmarks: [],
      superuser: false,
      banner: {
        dismissed: false,
      },
      banned: false,
    };
    return userRef.set(userData, { merge: true });
  }

  updateEmail(e): Promise<void> {
    const user = firebase.auth().currentUser;
    return user.updateEmail(e);
  }
  updatePassword(p): Promise<void> {
    const user = firebase.auth().currentUser;
    return user.updatePassword(p);
  }

  getUser(uid) {
    const userRef: AngularFirestoreDocument<any> = this.afs.doc(`/users/${uid}`);
    return new Promise((resolve, reject) => {
      userRef.get().subscribe(
        (ud) => {
          resolve(ud.data());
        },
        (e) => reject(e)
      );
    });
  }

  saveUser(uid, d) {
    const userRef: AngularFirestoreDocument<any> = this.afs.doc(`users/${uid}`);
    return userRef.update(d);
  }

  getMailchimpMember(email) {
    return this.http.get(`https://us-central1-ocean-cdr.cloudfunctions.net/mailchimp/member/${email}`);
  }

  addOrUpdateMailchimpMember(first, last, email, interests, isNew) {
    return this.http.post('https://us-central1-ocean-cdr.cloudfunctions.net/mailchimp/member/add-or-update', { firstName: first, lastName: last, email: email, interests: interests, newMember: isNew });
  }

  updateMailchimpInterests(interestsArray: Array<any>, userEmail: string) {
    return this.http.post('https://us-central1-ocean-cdr.cloudfunctions.net/mailchimp/member/update-interests', { email: userEmail, interests: interestsArray });
  }

  getMembers() {
    return this.afs
      .collection('users')
      .get()
      .pipe(
        map((snapshot) => {
          const items = [];
          snapshot.docs.map((a) => {
            const data = a.data();
            items.push(data);
          });
          return items;
        })
      );
  }

  deleteUser(uid) {
    const userRef: AngularFirestoreDocument<any> = this.afs.doc(`users/${uid}`);
    return userRef.delete();
  }
}
